import { useSelector } from "react-redux";
import { Table, Dropdown } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";

const PollsAndSurveysHomeTable = ({
  setSelectedPolls,
  polls = [],
  loading,
  onDelete,
  onModify,
}) => {
  const userProfile = useSelector(selectUserProfile);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedPolls(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  const tableData = polls.map((poll) => ({
    key: poll._id,
    id: poll.id,
    _id: poll._id,
    title: poll.name,
    description: poll.description,
    schedule: poll.schedule,
    createdAt: new Date(poll.createdAt).toLocaleDateString(),
    // Placeholder data for missing fields
    creator: userProfile.fullname,
    creatorImage: userProfile.profileImage,
    type: "Poll",
    status: "Published",
  }));

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? tableData.filter(
        (item) =>
          item.title?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.description
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.creator
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.type?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : tableData;

  const handleMenuClick = (menuItem, record) => {
    if (menuItem.key === "delete") {
      onDelete(record.key, record.title);
    } else if (menuItem.key === "modify") {
      onModify(record.key);
    }
  };

  return (
    <>
      <div className="overflow-x-auto" data-testid="polls-table">
        <Table
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          dataSource={filteredData}
          loading={loading}
          rowKey="id">
          <Column
            title="Title and Descritption"
            dataIndex="title"
            key="title"
            render={(_, { _id, title, description, type }) => (
              <Link
                to={
                  type.toLowerCase() === "political stance"
                    ? `/dashboard/polls/political-stance/${_id}`
                    : `/dashboard/polls/${_id}`
                }
                className="flex flex-col justify-start text-left align-start whitespace-nowrap">
                <h3 className="mt-1 text-bold font-bold">{title}</h3>
                <p className="text-small text-darkGrey">{description}</p>
              </Link>
            )}
          />
          <Column
            title="Creator"
            key="creator"
            dataIndex="creator"
            render={(_, { _id, creator, creatorImage, type }) => (
              <Link
                to={
                  type.toLowerCase() === "political stance"
                    ? `/dashboard/polls/political-stance/${_id}`
                    : `/dashboard/polls/${_id}`
                }
                className="flex items-center gap-5 text-small font-bold">
                <Avatar src={creatorImage} alt={creator || "Creator"} size={40} />
                <p className="text-regular font-bold">{creator}</p>
              </Link>
            )}
          />
          <Column
            title="Type"
            key="type"
            dataIndex="type"
            render={(_, { _id, type }) => (
              <Link
                to={
                  type.toLowerCase() === "political stance"
                    ? `/dashboard/polls/political-stance/${_id}`
                    : `/dashboard/polls/${_id}`
                }
                className="flex items-center gap-5 text-small font-bold">
                <p className="font-bold text-small">{type}</p>
              </Link>
            )}
          />
          <Column
            title="Status"
            key="status"
            dataIndex="status"
            render={(_, { _id, status, type }) => (
              <Link
                to={
                  type.toLowerCase() === "political stance"
                    ? `/dashboard/polls/political-stance/${_id}`
                    : `/dashboard/polls/${_id}`
                }
                className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <p className="text-primary text-small font-bold">{status}</p>
              </Link>
            )}
          />
          <Column
            title=""
            key="action"
            render={(_, record) => (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "modify",
                      label: "Modify",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "modify" }, record);
                      },
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "delete" }, record);
                      },
                    },
                  ],
                }}
                trigger={["click"]}>
                <CustomMoreOptions
                  variant="filled"
                  onClick={(e) => e.stopPropagation()}
                  color="black"
                  orientation="vertical"
                  size="small"
                />
              </Dropdown>
            )}
          />
        </Table>
      </div>
    </>
  );
};

PollsAndSurveysHomeTable.propTypes = {
  setSelectedPolls: PropTypes.func,
  polls: PropTypes.array,
  loading: PropTypes.bool,
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
};

export default PollsAndSurveysHomeTable;
